import { useState } from "react";
import { Star, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/AppContext";
import { Input } from "@/components/ui/input";

const Stars = ({ value, onChange, size = "h-5 w-5", testPrefix }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={testPrefix ? `${testPrefix}-${n}` : undefined}
        className="transition-transform duration-150 enabled:hover:scale-125 disabled:cursor-default">
        <Star className={`${size} ${n <= value ? "fill-yellow-300 text-yellow-300" : "text-slate-600"}`} />
      </button>
    ))}
  </div>
);

const Given = ({ label, f, testId }) => (
  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid={testId}>
    <span className="font-mono text-[10px] uppercase tracking-widest">{label}</span>
    <Stars value={f.rating} size="h-3.5 w-3.5" />
    <span className="text-slate-500">{f.by}</span>
    {f.comment && <span className="inline-flex items-center gap-1 italic text-slate-300"><MessageSquareQuote className="h-3 w-3" />{f.comment}</span>}
  </div>
);

export const InterviewFeedback = ({ iv, r, onSaved }) => {
  const { t } = useLang();
  const pending = r.my_sides.filter((s) => !iv.feedback?.[s]);
  const [chosen, setChosen] = useState(null);
  const side = pending.includes(chosen) ? chosen : pending[0];
  const setSide = setChosen;
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const started = Date.now() >= new Date(iv.start).getTime();
  const counterpart = side === "person" ? r.company.name : r.person.name;

  const submit = async () => {
    try {
      await api.post(`/interviews/${iv.id}/feedback`, { rating, comment, side });
      toast.success(t("feedback_sent"));
      setRating(0); setComment("");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-2 space-y-2 border-t border-white/5 pt-2" data-testid={`interview-feedback-${iv.id}`}>
      {Object.entries(iv.feedback || {}).map(([s, f]) => (
        <Given key={s} f={f} testId={`feedback-given-${iv.id}-${s}`} label={r.my_sides.includes(s) ? t("feedback_given") : t("feedback_received")} />
      ))}
      {started && pending.length > 0 && side && (
        <div className="space-y-2" data-testid={`feedback-form-${iv.id}`}>
          {pending.length > 1 && (
            <div className="flex items-center gap-2 text-xs text-slate-400">{t("rate_as")}
              {pending.map((s) => (
                <button key={s} onClick={() => setSide(s)} data-testid={`feedback-side-${s}`}
                  className={`rounded-full border px-2.5 py-0.5 ${side === s ? "border-emerald-400 text-emerald-300" : "border-white/10"}`}>{t(s)}</button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-slate-300">{t("rate_counterpart").replace("{name}", counterpart)}</span>
            <Stars value={rating} onChange={setRating} testPrefix={`feedback-star-${iv.id}`} />
          </div>
          <div className="flex gap-2">
            <Input value={comment} maxLength={500} onChange={(e) => setComment(e.target.value)} placeholder={t("feedback_comment")} data-testid={`feedback-comment-${iv.id}`} />
            <button disabled={!rating} onClick={submit} data-testid={`feedback-submit-${iv.id}`}
              className="shrink-0 rounded-full bg-emerald-500 px-4 text-xs font-semibold text-slate-950 hover:opacity-90 disabled:opacity-40">{t("send_feedback")}</button>
          </div>
        </div>
      )}
    </div>
  );
};
